<script setup lang="ts">
/**
 * CalendlyButton — bouton « Nous contacter / Planifier un appel » avec bordure
 * animée BLEUE Calendly (#006BFF) et icône calendrier. Remplace les anciens
 * boutons WhatsApp à bordure verte (header déroulant, footer, CTA…).
 * Clic → ouvre le modal Calendly global (useCalendlyModal). Karim 2026-08-05.
 *
 * Dimensionnement par le parent via `class` (fallthrough) : ex. `w-full px-5
 * py-3` dans le header déroulant, `px-6 py-2.5` dans le footer.
 */
withDefaults(
  defineProps<{
    label?: string;
    /** Taille du rond icône (classes tailwind). */
    iconWrapperClass?: string;
  }>(),
  {
    label: "Nous contacter",
    iconWrapperClass: "w-8 h-8",
  },
);

const { openCalendly } = useCalendlyModal();
</script>

<template>
  <button
    type="button"
    class="calendly-btn group/btn relative inline-flex items-center justify-center gap-3 bg-white text-gray-800 font-medium rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-[#006BFF]/20 cursor-pointer"
    @click="openCalendly"
  >
    <!-- Bordure dégradée animée (bleu Calendly) -->
    <span class="calendly-border"></span>
    <!-- Icône calendrier sur rond dégradé bleu -->
    <span
      class="relative z-10 flex items-center justify-center rounded-full bg-gradient-to-br from-[#006BFF] to-[#1f9afe]"
      :class="iconWrapperClass"
    >
      <UIcon name="i-lucide-calendar-days" class="w-4 h-4 text-white" />
    </span>
    <span class="relative z-10">{{ label }}</span>
  </button>
</template>

<style scoped>
.calendly-btn {
  position: relative;
  overflow: hidden;
  border: 2px solid transparent;
  background-clip: padding-box;
}

/* Bande de dégradé 3× plus large que le bouton, qui GLISSE (transform) :
   même rendu qu'avant, mais animé par la carte graphique. Avant, la position
   du fond (`background-position`) était animée : le navigateur redessinait à
   chaque image, sur toutes les pages (header + pied de page), même hors
   écran — saccades sur mobile (2026-10-13). La forme ronde vient du bouton
   (rounded-full + overflow hidden). */
.calendly-border {
  position: absolute;
  top: -2px;
  bottom: -2px;
  left: -2px;
  width: calc(300% + 12px);
  background: linear-gradient(
    90deg,
    #006bff,
    #1f9afe,
    #0047b3,
    #1f9afe,
    #006bff
  );
  animation: calendly-gradient 3s ease infinite;
  will-change: transform;
  z-index: 0;
}

.calendly-btn::before {
  content: "";
  position: absolute;
  inset: 2px;
  background: white;
  border-radius: 9999px;
  z-index: 1;
}

@keyframes calendly-gradient {
  0% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(-66.667%, 0, 0);
  }
  100% {
    transform: translate3d(0, 0, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .calendly-border {
    animation: none;
  }
}

.calendly-btn:hover .calendly-border {
  animation-duration: 1.5s;
}
</style>
